import { useMutation } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import Button from "@components/buttons/Button";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import styles from "@components/expertSection/schedule/ScheduleSession.module.css";
import InviteAccountMembersForm from "@components/forms/InviteAccountMembersForm";
import Modal from "@components/Modal";
import Nbsp from "@components/Nbsp";
import IconBase from "@components/text/IconBase";
import TextWithIcon from "@components/text/TextWithIcon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import ExternalLink from "@components/typography/ExternalLink";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useCareTeamInviteCreateMutation from "@hooks/useCareTeamInviteCreateMutation";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import {
  ConsultAvailabilityFragment as ConsultAvailabilityType,
  ConsultDurationEnum,
  ConsultFragment,
  ConsultUpdateDocument,
  ConsultUpdateMutation,
  ConsultUpdateMutationVariables,
  ContactMethodEnum,
  GetJourneyWithExpertAndConsultsQuery
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";
import { truncate } from "@utils/stringUtils";

type Props = {
  consult: ConsultFragment;
  handleClose?: () => void;
  journey: GetJourneyWithExpertAndConsultsQuery["journey"];
  originalDuration: ConsultDurationEnum;
  originalStartTime: Date;
  rescheduledConsult: ConsultFragment;
  setAvailabilitySelected: (availability: ConsultAvailabilityType | null) => void;
  setRescheduledConsult: (consult: ConsultFragment | null) => void;
};

const SessionRescheduledConfirmation = ({
  consult,
  handleClose,
  journey,
  originalDuration,
  originalStartTime,
  rescheduledConsult,
  setAvailabilitySelected,
  setRescheduledConsult
}: Props) => {
  const intl = useIntl();
  const rescheduledRef = useRef<HTMLDivElement>(null);

  const careTeamInviteCreate = useCareTeamInviteCreateMutation(journey);
  const myClient = useMyClient();
  const [updateConsult] = useMutation<ConsultUpdateMutation, ConsultUpdateMutationVariables>(ConsultUpdateDocument);
  const inviteOthersOpener = useOpener();

  const openUrlLink = useCallback(() => {
    if (consult.joinUrl) {
      window.open(consult.joinUrl, "_blank", "noopener");
    }
  }, [consult]);

  const getInvites = useCallback(
    (consult: ConsultFragment) => ({
      careTeamInvites: consult.invites.filter(invite => invite.careTeamInvite),
      clients: consult.invites.filter(invite => invite.client)
    }),
    []
  );

  const inviteeOptions = useMemo(() => {
    const otherClientRoles = journey.clientRoles.filter(role => role.client.id !== myClient?.id);

    return otherClientRoles.map(option => ({
      label: option.client.fullName,
      value: option.client.id
    }));
  }, [journey.clientRoles, myClient?.id]);

  const handleAddInvites = async (emails: string[], clientIds: string[]) => {
    const newEmails = emails.filter(
      email =>
        !journey.careTeamInvites?.some(cti => cti.email === email) &&
        !journey.clientRoles.some(role => role.client.email.email === email)
    );

    const careTeamInvites = await Promise.all(
      newEmails.map(async email =>
        careTeamInviteCreate({
          variables: {
            email,
            journeyId: journey.id
          }
        }).then(response => response)
      )
    );

    const invitedCareTeamInvites = consult.invites
      .filter(invite => invite.careTeamInvite)
      .map(invite => invite.careTeamInvite?.id ?? "")
      .concat(careTeamInvites.map(invite => invite.data?.careTeamInviteCreate?.careTeamInvite?.id ?? ""));

    const newClientIds = clientIds;

    updateConsult({
      variables: {
        id: consult.id,
        invitedCareTeamInviteIds: invitedCareTeamInvites,
        invitedClientIds: newClientIds
      }
    });
  };

  useEffect(() => {
    if (rescheduledConsult) {
      rescheduledRef?.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [rescheduledConsult]);

  return (
    <>
      <div className={styles.rescheduleContainer} ref={rescheduledRef}>
        <div className={styles.rescheduleHeader}>
          <Heading2>
            <FormattedMessage id="pages.consults.reschedule.meetingScheduled" />
          </Heading2>
          {handleClose && (
            <div className={styles.close}>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.close"
                data-test="close-reschedule"
                icon="times"
                onClick={handleClose}
              />
            </div>
          )}
          <div className={styles.spacingTop}>
            <BodyCopy color="gray-500">
              <FormattedMessage id="pages.consults.reschedule.weEmailed" />
            </BodyCopy>
          </div>
        </div>
        <div className={styles.rescheduledContainer}>
          <div className={styles.grid}>
            <div className={styles.row}>
              <div className={styles.left}>
                <BodyCopy color="gray-500">
                  <FormattedMessage id="pages.consults.reschedule.updated.what" />
                </BodyCopy>
              </div>
              <BodyCopy>
                <FormattedMessage
                  id="pages.consults.reschedule.updated.withExpert"
                  values={{ name: consult.expert.firstName }}
                />
              </BodyCopy>
            </div>
            <div className={styles.row}>
              <div className={styles.left}>
                <BodyCopy color="gray-500">
                  <FormattedMessage id="pages.consults.reschedule.updated.when" />
                </BodyCopy>
              </div>
              <div>
                <div className={styles.crossedOut}>
                  <BodyCopy color="gray-500">
                    <DateAndTimeStamp value={originalStartTime} />
                    <FormattedMessage id="dictionary.punctuation.comma" />
                    <Nbsp />
                    <FormattedMessage
                      id={`pages.consults.reschedule.${
                        originalDuration === ConsultDurationEnum.ONE_HOUR ? "min60" : "min30"
                      }`}
                    />
                  </BodyCopy>
                </div>
                <div>
                  <BodyCopy>
                    <DateAndTimeStamp value={consult.startTime} />
                    <FormattedMessage id="dictionary.punctuation.comma" />
                    <Nbsp />
                    <FormattedMessage
                      id={`pages.consults.reschedule.${
                        consult.durationType === ConsultDurationEnum.ONE_HOUR ? "min60" : "min30"
                      }`}
                    />
                  </BodyCopy>
                </div>
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.left}>
                <BodyCopy color="gray-500">
                  <FormattedMessage id="pages.consults.reschedule.updated.where" />
                </BodyCopy>
              </div>
              <div>
                {consult.contactMethod === ContactMethodEnum.ZOOM ? (
                  <ButtonLink disabled={!consult.joinUrl} onClick={openUrlLink}>
                    {consult.joinUrl ? (
                      truncate(consult.joinUrl, 36)
                    ) : (
                      <TextWithIcon
                        icon="spinner"
                        text={intl.formatMessage({ id: "pages.consults.reschedule.updated.creatingMeeting" })}
                      />
                    )}
                  </ButtonLink>
                ) : consult.phoneNumber?.formattedNumber ? (
                  <ExternalLink href={`tel:${consult.phoneNumber?.number ?? ""}`}>
                    {consult.phoneNumber.formattedNumber}
                  </ExternalLink>
                ) : (
                  <TextWithIcon
                    icon="spinner"
                    text={intl.formatMessage({ id: "pages.consults.reschedule.updated.creatingMeeting" })}
                  />
                )}
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.left}>
                <BodyCopy color="gray-500">
                  <FormattedMessage id="pages.consults.reschedule.updated.who" />
                </BodyCopy>
              </div>
              <div>
                {getInvites(consult).clients.map(invite => (
                  <div className={styles.rescheduledInvite} key={invite.id}>
                    <div className={styles.rescheduledInviteName}>
                      <BodyCopy>{invite.client?.fullName}</BodyCopy>
                    </div>

                    <BodyCopy color="gray-500" size="small">
                      {invite.client?.email.email ?? invite.careTeamInvite?.email ?? ""}
                    </BodyCopy>
                  </div>
                ))}
                {getInvites(consult).careTeamInvites.map(invite => (
                  <div className={styles.rescheduledInvite} key={invite.id}>
                    <BodyCopy>{invite.client?.email.email ?? invite.careTeamInvite?.email ?? ""}</BodyCopy>
                  </div>
                ))}
                <div className={styles.inviteOthers}>
                  <Button fill="clear" onClick={inviteOthersOpener.open}>
                    <FormattedMessage id="pages.consults.reschedule.updated.inviteOthers" />
                    <IconBase icon="add" slot="end" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.makeChanges}>
          <FormattedMessage
            id="pages.consults.reschedule.updated.makeChanges"
            values={{
              cancel: (
                <InternalLink
                  to={journeyRoute({
                    journeyId: journey.id,
                    linkAfterJourney: `/consults/${consult.id}/cancel`,
                    myClient
                  })}
                >
                  <FormattedMessage id="pages.consults.reschedule.updated.cancel" />
                </InternalLink>
              ),
              reschedule: (
                <ButtonLink
                  onClick={() => {
                    setRescheduledConsult(null);
                    setAvailabilitySelected(null);
                  }}
                >
                  <FormattedMessage id="pages.consults.reschedule.updated.reschedule" />
                </ButtonLink>
              )
            }}
          />
        </div>
      </div>
      <Modal
        opener={inviteOthersOpener}
        title={intl.formatMessage({ id: "models.client.inviteAccountMembers.consultTitle" })}
      >
        {({ modalDisplayed }) => (
          <InviteAccountMembersForm
            autofocus={modalDisplayed}
            consultScheduling={{
              inviteOptions: inviteeOptions,
              selectedClientIds:
                consult.invites.filter(invite => invite.client).map(invite => invite.client?.id as string) ?? []
            }}
            journey={journey}
            onCancel={inviteOthersOpener.close}
            onSuccess={(emails, clientIds) => {
              if (emails && clientIds) {
                handleAddInvites(emails, clientIds);
              }

              inviteOthersOpener.close();
            }}
            saveKey="dictionary.add"
          />
        )}
      </Modal>
    </>
  );
};

export default SessionRescheduledConfirmation;
